import { useCallback, useEffect, useRef, useState } from 'react';
import { FaHeart, FaRegHeart, FaThLarge, FaListUl, FaTimes, FaChevronLeft, FaChevronRight, FaImages } from 'react-icons/fa';
import logo from '../images/logo.png';
import { company } from '../data';
import { Container, PageHero } from '../components/ui';
import useAsync from '../blog/useAsync';
import { cfg } from '../runtimeConfig';

// The image service (gallery folder). Images are managed there; this page only reads the public JSON.
const GALLERY_BASE = cfg('GALLERY_BASE', process.env.REACT_APP_GALLERY_BASE).replace(/\/$/, '');
const GALLERY_FOLDER = cfg('GALLERY_FOLDER', process.env.REACT_APP_GALLERY_FOLDER) || 'site-gallery';
const MAX_IMAGES = 50;
const TIMEOUT_MS = 8000;
const LIKES_KEY = 'anuryan-gallery-likes';

async function fetchGallery() {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  let res;
  try {
    res = await fetch(`${GALLERY_BASE}/images/api/public/folders/${encodeURIComponent(GALLERY_FOLDER)}`, { signal: controller.signal });
  } catch {
    throw new Error('offline');
  } finally {
    clearTimeout(timer);
  }
  if (!res.ok) throw new Error('server');
  const data = await res.json();
  return (data.images || []).slice(0, MAX_IMAGES).map((img) => ({
    ...img,
    src: /^https?:\/\//.test(img.url) ? img.url : `${GALLERY_BASE}${img.url}`,
  }));
}

// "Likes" are a personal bookmark kept in this browser only; there are no counts and no comments.
function useLikes() {
  const [liked, setLiked] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(LIKES_KEY) || '[]')); } catch { return new Set(); }
  });
  const toggle = useCallback((id) => {
    setLiked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      try { localStorage.setItem(LIKES_KEY, JSON.stringify([...next])); } catch { /* storage unavailable */ }
      return next;
    });
  }, []);
  return [liked, toggle];
}

const Avatar = ({ size = 'w-10 h-10' }) => (
  <span className={`${size} shrink-0 rounded-full p-[2px] bg-gradient-to-tr from-secondary via-primary to-[#4cc3ff]`}>
    <span className="block w-full h-full rounded-full bg-white p-0.5">
      <img src={logo} alt="" className="w-full h-full object-contain rounded-full" />
    </span>
  </span>
);

const Actions = ({ img, liked, onLike }) => (
  <div className="flex items-center gap-4 text-secondary">
    <button type="button" onClick={() => onLike(img.id)} aria-pressed={liked} aria-label={liked ? 'Remove like' : 'Like'}
      className={`p-1 -m-1 transition active:scale-90 ${liked ? 'text-rose-500' : 'hover:text-primary'}`}>
      {liked ? <FaHeart size={22} /> : <FaRegHeart size={22} />}
    </button>
  </div>
);

const Caption = ({ img }) =>
  img.description ? (
    <p className="text-sm text-slate-800 leading-relaxed break-words">
      <span className="font-semibold text-secondary mr-1.5">anuryan</span>{img.description}
    </p>
  ) : null;

const PostCard = ({ img, liked, onLike, onOpen }) => (
  <article className="rounded-2xl bg-white border border-slate-200 overflow-hidden shadow-sm">
    <header className="flex items-center gap-3 px-4 py-3">
      <Avatar />
      <div className="min-w-0">
        <p className="text-sm font-semibold text-secondary leading-tight">anuryan</p>
        <p className="text-xs text-slate-500 truncate">{company.name}</p>
      </div>
    </header>
    <button type="button" onClick={onOpen} className="block w-full bg-slate-100" aria-label="Open image full screen">
      <img src={img.src} alt={img.description || 'Anuryan E&A'} width={img.width} height={img.height} loading="lazy"
        className="w-full h-auto max-h-[80vh] object-contain mx-auto" />
    </button>
    <div className="px-4 py-3 space-y-2">
      <Actions img={img} liked={liked} onLike={onLike} />
      <Caption img={img} />
    </div>
  </article>
);

const Lightbox = ({ images, index, setIndex, liked, onLike, onClose }) => {
  const img = images[index];
  const closeRef = useRef(null);
  const prev = () => setIndex((i) => (i - 1 + images.length) % images.length);
  const next = () => setIndex((i) => (i + 1) % images.length);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    const opener = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    closeRef.current?.focus();
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      opener?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Swipe left/right on touch screens.
  const touchX = useRef(null);
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 50) (dx < 0 ? next : prev)();
    touchX.current = null;
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/90 flex flex-col lg:flex-row" role="dialog" aria-modal="true" aria-label="Photo viewer"
      onClick={onClose} onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }} onTouchEnd={onTouchEnd}>
      <button ref={closeRef} type="button" onClick={onClose} aria-label="Close"
        className="absolute top-3 right-3 z-10 w-11 h-11 grid place-items-center rounded-full bg-black/50 text-white hover:bg-black/70"><FaTimes /></button>

      <div className="relative flex-1 min-h-0 grid place-items-center p-3 md:p-8" >
        <img src={img.src} alt={img.description || 'Anuryan E&A'} onClick={(e) => e.stopPropagation()}
          className="max-w-full max-h-full w-auto h-auto object-contain" />
        {images.length > 1 && (
          <>
            <button type="button" onClick={(e) => { e.stopPropagation(); prev(); }} aria-label="Previous photo"
              className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 w-11 h-11 grid place-items-center rounded-full bg-white/15 text-white hover:bg-white/30"><FaChevronLeft /></button>
            <button type="button" onClick={(e) => { e.stopPropagation(); next(); }} aria-label="Next photo"
              className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 w-11 h-11 grid place-items-center rounded-full bg-white/15 text-white hover:bg-white/30"><FaChevronRight /></button>
          </>
        )}
      </div>

      <aside onClick={(e) => e.stopPropagation()} className="bg-white lg:w-96 shrink-0 max-h-[40vh] lg:max-h-none overflow-y-auto">
        <header className="flex items-center gap-3 px-4 py-3 border-b border-slate-200">
          <Avatar />
          <p className="text-sm font-semibold text-secondary">anuryan</p>
        </header>
        <div className="p-4 space-y-3">
          <Caption img={img} />
          {!img.description && <p className="text-sm text-slate-400">No description.</p>}
          <Actions img={img} liked={liked.has(img.id)} onLike={onLike} />
          <p className="text-xs text-slate-400">{index + 1} of {images.length}</p>
        </div>
      </aside>
    </div>
  );
};

const Skeleton = () => (
  <div className="grid grid-cols-3 gap-1 sm:gap-2" aria-hidden>
    {Array.from({ length: 9 }).map((_, i) => <div key={i} className="aspect-square bg-slate-200 animate-pulse rounded-sm" />)}
  </div>
);

const Gallery = () => {
  const { loading, error, data, reload } = useAsync(fetchGallery, []);
  const [view, setView] = useState('feed');
  const [open, setOpen] = useState(null);
  const [liked, toggleLike] = useLikes();
  const images = data || [];

  useEffect(() => {
    document.title = 'Gallery | Anuryan E&A Pvt. Ltd.';
  }, []);

  return (
    <>
      <PageHero eyebrow="Gallery" title="Recent work & events" text="Moments from site, panel rooms and our team, as they happen." />
      <section className="py-10 md:py-14 bg-mist">
        <Container className="max-w-4xl">
          <div className="flex items-center gap-4 md:gap-8 pb-6">
            <Avatar size="w-20 h-20 md:w-28 md:h-28" />
            <div className="min-w-0">
              <h2 className="font-display font-bold text-xl md:text-2xl text-secondary">anuryan</h2>
              <p className="text-sm text-slate-600">{company.name}</p>
              <p className="text-sm mt-1"><strong className="text-secondary">{images.length}</strong> <span className="text-slate-600">posts</span></p>
            </div>
          </div>

          <div role="tablist" aria-label="Gallery layout" className="flex border-t border-slate-300">
            {[['feed', 'Feed', FaListUl], ['grid', 'Grid', FaThLarge]].map(([id, label, Icon]) => (
              <button key={id} role="tab" aria-selected={view === id} onClick={() => setView(id)}
                className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold uppercase tracking-wider border-t-2 -mt-px transition ${view === id ? 'border-secondary text-secondary' : 'border-transparent text-slate-400 hover:text-secondary'}`}>
                <Icon /> {label}
              </button>
            ))}
          </div>

          <div className="mt-4">
            {loading && <Skeleton />}

            {error && (
              <div className="text-center py-16" role="alert">
                <h3 className="font-display font-semibold text-xl text-secondary">The gallery is temporarily unavailable</h3>
                <p className="text-slate-600 mt-2">We could not load the photos right now. Please try again in a few minutes.</p>
                <button type="button" onClick={reload} className="mt-5 rounded-full bg-primary hover:bg-[#0aa0ee] text-white font-semibold px-6 py-2.5 transition">Try again</button>
              </div>
            )}

            {!loading && !error && images.length === 0 && (
              <div className="text-center py-16 text-slate-500">
                <FaImages className="mx-auto mb-3" size={36} />
                <p className="font-semibold text-secondary">No posts yet</p>
                <p className="text-sm">Photos of our recent work and events will appear here.</p>
              </div>
            )}

            {images.length > 0 && view === 'grid' && (
              <ul className="grid grid-cols-3 gap-1 sm:gap-2">
                {images.map((img, i) => (
                  <li key={img.id}>
                    <button type="button" onClick={() => setOpen(i)} aria-label={img.description ? `Open: ${img.description}` : 'Open photo'}
                      className="group relative block w-full aspect-square overflow-hidden bg-slate-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary">
                      <img src={img.src} alt={img.description || 'Anuryan E&A'} loading="lazy"
                        className="w-full h-full object-cover transition group-hover:scale-105" />
                      <span className="absolute inset-0 bg-secondary/0 group-hover:bg-secondary/50 transition grid place-items-center">
                        {liked.has(img.id) && <FaHeart className="text-white drop-shadow" size={22} />}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {images.length > 0 && view === 'feed' && (
              <div className="max-w-xl mx-auto space-y-6">
                {images.map((img, i) => (
                  <PostCard key={img.id} img={img} liked={liked.has(img.id)} onLike={toggleLike} onOpen={() => setOpen(i)} />
                ))}
              </div>
            )}
          </div>
        </Container>
      </section>

      {open != null && (
        <Lightbox images={images} index={open} setIndex={setOpen} liked={liked} onLike={toggleLike} onClose={() => setOpen(null)} />
      )}
    </>
  );
};

export default Gallery;
